import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import DraggableDialog from 'components/common/DraggableDialog';
import { loadFormTemplateBytes } from 'system/formTemplateFileStore';

const Host = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  position: relative;
  border: 1px solid var(--erp-border);
  border-radius: 4px;
  overflow: hidden;
  background: var(--erp-color-bg-white);

  & > iframe {
    position: absolute !important;
    inset: 0 !important;
    width: 100% !important;
    height: 100% !important;
    border: none;
  }
`;

const Status = styled.p`
  margin: 0;
  flex: 0 0 auto;
  font-size: 11px;
  color: var(--erp-text-secondary);
`;

/**
 * 양식 HWP 미리보기 (rhwp)
 * — publicUrl 또는 IndexedDB 업로드분
 */
const BizFormHwpPreviewDialog = ({ open, onClose, form, prefsUserId = '' }) => {
  const hostRef = useRef(null);
  const editorRef = useRef(null);
  const [status, setStatus] = useState('준비 중…');

  useEffect(() => {
    if (!open || !form?.formId) {
      return undefined;
    }

    let cancelled = false;

    (async () => {
      setStatus('에디터 로딩…');
      try {
        const mod = await import('@rhwp/editor');
        if (cancelled || !hostRef.current) return;

        if (editorRef.current?.destroy) {
          try {
            editorRef.current.destroy();
          } catch (_) {
            /* ignore */
          }
          editorRef.current = null;
        }

        const editor = await mod.createEditor(hostRef.current, {
          width: '100%',
          height: '100%',
        });
        if (cancelled) {
          editor.destroy?.();
          return;
        }
        editorRef.current = editor;

        setStatus(`양식 로드 중… ${form.templateFile || form.formId}`);
        const loaded = await loadFormTemplateBytes(form);
        if (cancelled) return;
        const result = await editor.loadFile(loaded.bytes, loaded.fileName, {
          suppressDialogs: true,
        });
        if (cancelled) return;
        setStatus(
          `${form.formName || form.formId} · ${loaded.source} · pages=${result?.pageCount ?? '?'} · 필드 ${(form.fields || []).length}개`,
        );
      } catch (e) {
        console.error('[BizFormHwpPreview]', e);
        if (!cancelled) {
          setStatus(`미리보기 실패: ${e?.message || e}`);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (editorRef.current?.destroy) {
        try {
          editorRef.current.destroy();
        } catch (_) {
          /* ignore */
        }
      }
      editorRef.current = null;
    };
  }, [open, form]);

  const title = form
    ? `양식 미리보기 — ${form.formName || form.formId}`
    : '양식 미리보기';

  return (
    <DraggableDialog
      open={open}
      onClose={onClose}
      title={title}
      dialogWidth="min(1100px, 96vw)"
      dialogHeight="min(820px, 92vh)"
      keepMounted={false}
      prefsUserId={prefsUserId}
      sizeKey="bizFormHwpPreview"
    >
      <Status>{status}</Status>
      <Host ref={hostRef} />
    </DraggableDialog>
  );
};

export default BizFormHwpPreviewDialog;
